// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { IntegrationStatus } from '@/components/integration-status';
import { VisibilityOffOutlined } from '@/constants/hardfin-standard-icons';
import { Sort } from '@/constants/sort';
import { ProjectSortProperty } from '@/project/api/get-projects';
import { Project } from '@/project/api/types';
import { ProjectAssignmentsLabel } from '@/project/components/project-assignments-label';
import { ProjectBillingChart } from '@/project/components/project-billing-chart';
import { ProjectDateRangeControl } from '@/project/components/project-date-range-control';
import { ProjectEndDateLabel } from '@/project/components/project-end-date-label';
import { ProjectLink } from '@/project/components/project-link';
import { ProjectLocationsLabel } from '@/project/components/project-locations-label';
import { ProjectStartDateLabel } from '@/project/components/project-start-date-label';
import { ProjectTableSkeleton } from '@/project/components/project-table-skeleton';
import { WorkflowStageIndicator } from '@/project/components/workflow-stage-indicator';
import { deriveProjectCurrency } from '@/project/utilities/project-currency';
import { hasIntegration } from '@/utilities/integration';
import {
  Box,
  Paper,
  Stack,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Suspense } from 'react';

export type ProjectTableColumn =
  | 'name'
  | 'integration'
  | 'customer'
  | 'locations'
  | 'assets'
  | 'end-date'
  | 'start-date'
  | 'timeline'
  | 'workflow-stage'
  | 'billing-summary';

const DEFAULT_COLUMNS: ProjectTableColumn[] = [
  'integration',
  'name',
  'timeline',
  'workflow-stage',
  'locations',
  'assets',
  'billing-summary',
];

const LABELS: Record<ProjectTableColumn, string> = {
  integration: '',
  name: 'Project',
  timeline: 'Timeline',
  'workflow-stage': 'Workflow stage',
  customer: 'Customer',
  locations: 'Locations',
  assets: 'Fulfillment',
  'start-date': 'Start date',
  'end-date': 'End date',
  'billing-summary': 'Billing summary',
};

const SX: Partial<Record<ProjectTableColumn, TableCellProps['sx']>> = {
  integration: { width: '3rem', pl: 2, pr: 0, whiteSpace: 'nowrap' },
  timeline: {
    minWidth: '16rem',
    width: '18rem',
    maxWidth: '20rem',
  },
  'workflow-stage': {
    display: { xs: 'none', lg: 'table-cell' },
    minWidth: '10rem',
    width: '12rem',
  },
  customer: { display: { xs: 'none', sm: 'table-cell' } },
  locations: { display: { xs: 'none', md: 'table-cell' } },
  assets: { display: { xs: 'none', sm: 'table-cell' } },
  'billing-summary': {
    display: { xs: 'none', lg: 'table-cell' },
    width: '14rem',
  },
};

const ALIGNMENT: Partial<Record<ProjectTableColumn, TableCellProps['align']>> =
  {
    integration: 'right',
    'start-date': 'right',
    'billing-summary': 'center',
    'workflow-stage': 'center',
  };

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: 'numeric',
  year: 'numeric',
};

const DEFAULT_EMPTY_STATE = 'No projects';

interface Props {
  /**
   * A list of projects to display in the table.
   */
  projects: Project[];

  /**
   * A list of columns to display in the table.
   *
   * @default ['name', 'customer', 'locations', 'assets', 'dates']
   */
  columns?: ProjectTableColumn[];

  /**
   * The current sort state of the table.
   */
  sort: Sort<ProjectSortProperty>;

  /**
   * If provided, the message to display when there are no projects.
   *
   * @default 'No projects'
   */
  emptyState?: string;

  /**
   * A callback fired when a sort header is clicked.
   */
  onSort?: (property: ProjectSortProperty) => void;

  /**
   * The style props of the table.
   */
  sx?: SxProps;
}

export function ProjectTable(props: Props) {
  const queryClient = useQueryClient();
  const columns = props.columns ?? DEFAULT_COLUMNS;
  const emptyState = props.emptyState ?? DEFAULT_EMPTY_STATE;

  const isLabelActive = (column: ProjectTableColumn) => {
    switch (column) {
      case 'name':
        return props.sort.property === 'name';
      case 'start-date':
      case 'timeline':
        return props.sort.property === 'start';
      case 'end-date':
        return props.sort.property === 'end';
      case 'workflow-stage':
        return props.sort.property === 'workflow';

      case 'integration':
      case 'customer':
      case 'locations':
      case 'assets':
      case 'billing-summary':
        return false;

      default:
        return column satisfies never;
    }
  };

  const getSortDirection = (column: ProjectTableColumn) => {
    switch (column) {
      case 'name':
        return props.sort.property === 'name' ? props.sort.direction : false;
      case 'start-date':
      case 'timeline':
        return props.sort.property === 'start' ? props.sort.direction : false;
      case 'end-date':
        return props.sort.property === 'end' ? props.sort.direction : false;
      case 'workflow-stage':
        return props.sort.property === 'workflow'
          ? props.sort.direction
          : false;

      case 'integration':
      case 'customer':
      case 'locations':
      case 'assets':
      case 'billing-summary':
        return false;

      default:
        return column satisfies never;
    }
  };

  const getLabelDirection = (column: ProjectTableColumn) => {
    switch (column) {
      case 'name':
        return props.sort.property === 'name'
          ? props.sort.direction
          : undefined;
      case 'start-date':
      case 'timeline':
        return props.sort.property === 'start'
          ? props.sort.direction
          : undefined;
      case 'end-date':
        return props.sort.property === 'end' ? props.sort.direction : undefined;
      case 'workflow-stage':
        return props.sort.property === 'workflow'
          ? props.sort.direction
          : undefined;

      case 'integration':
      case 'customer':
      case 'locations':
      case 'assets':
      case 'billing-summary':
        return undefined;

      default:
        return column satisfies never;
    }
  };

  const createSortHandler = (column: ProjectTableColumn) => {
    if (props.sort === undefined) {
      return undefined;
    }

    switch (column) {
      case 'name':
        return () => props.onSort?.('name');
      case 'start-date':
      case 'timeline':
        return () => props.onSort?.('start');
      case 'end-date':
        return () => props.onSort?.('end');
      case 'workflow-stage':
        return () => props.onSort?.('workflow');

      case 'integration':
      case 'customer':
      case 'locations':
      case 'assets':
      case 'billing-summary':
        return undefined;

      default:
        return column satisfies never;
    }
  };
  const { user } = useCurrentUser();
  const hasCrmEnabled =
    hasIntegration(user.organization.enabledIntegrations, 'HUBSPOT') ||
    hasIntegration(user.organization.enabledIntegrations, 'SFDC');

  return (
    <Suspense fallback={<ProjectTableSkeleton columns={columns} />}>
      <TableContainer component={Paper} variant="outlined" sx={props.sx}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const sx = SX[column];
                const label = LABELS[column];
                const alignment = ALIGNMENT[column];
                const sortHandler = createSortHandler(column);

                if (column === 'integration' && !hasCrmEnabled) {
                  return null;
                }

                // Special header layout for the name column
                if (column === 'name') {
                  return (
                    <TableCell
                      key={column}
                      sx={sx}
                      align={alignment}
                      sortDirection={getSortDirection(column)}
                    >
                      <TableSortLabel
                        active={isLabelActive(column)}
                        onClick={sortHandler}
                        direction={getLabelDirection(column)}
                      >
                        Project
                      </TableSortLabel>
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        Customer
                      </Typography>
                    </TableCell>
                  );
                }

                if (sortHandler === undefined) {
                  return (
                    <TableCell
                      key={column}
                      sx={sx}
                      align={alignment}
                      sortDirection={getSortDirection(column)}
                    >
                      {label}
                    </TableCell>
                  );
                }

                return (
                  <TableCell
                    key={column}
                    sx={sx}
                    align={alignment}
                    sortDirection={getSortDirection(column)}
                  >
                    <TableSortLabel
                      active={isLabelActive(column)}
                      onClick={sortHandler}
                      direction={getLabelDirection(column)}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {props.projects.map((project) => (
              <TableRow key={project.id}>
                {columns.map((column) => {
                  const sx = SX[column];
                  const alignment = ALIGNMENT[column];

                  switch (column) {
                    case 'name':
                      return (
                        <TableCell key={column} sx={sx} align={alignment}>
                          <Stack spacing={0.5}>
                            <Stack
                              spacing={1}
                              direction="row"
                              alignItems="center"
                            >
                              <ProjectLink
                                variant="subtitle2"
                                project={project}
                              />
                              {project.isArchived && (
                                <Tooltip title="This project is archived" arrow>
                                  <VisibilityOffOutlined
                                    fontSize="small"
                                    color="disabled"
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                            <Typography
                              variant="body2"
                              color={
                                project.isArchived
                                  ? 'text.disabled'
                                  : 'text.secondary'
                              }
                            >
                              {project.customer === null
                                ? 'Not set'
                                : project.customer.name}
                            </Typography>
                          </Stack>
                        </TableCell>
                      );
                    case 'integration':
                      if (!hasCrmEnabled) {
                        return null;
                      }
                      return (
                        <TableCell key={column} sx={sx} align={alignment}>
                          <Box
                            sx={{ display: 'flex', justifyContent: 'center' }}
                          >
                            <IntegrationStatus
                              status={project.integrations}
                              entityType="project"
                              entityKey={project.key}
                              entityDisplayName={project.name}
                              onResolved={() => {
                                // Listing pages key on ['project', filter] — same
                                // prefix as the detail query. A prefix invalidate
                                // covers every list filter currently mounted.
                                void queryClient.invalidateQueries({
                                  queryKey: ['project'],
                                });
                              }}
                            />
                          </Box>
                        </TableCell>
                      );
                    case 'customer':
                      return (
                        <TableCell key={column} sx={sx} align={alignment}>
                          {project.customer?.name ?? '—'}
                        </TableCell>
                      );
                    case 'locations':
                      return (
                        <TableCell key={column} sx={sx} align={alignment}>
                          <ProjectLocationsLabel
                            locations={project.locations}
                            isArchived={project.isArchived}
                            maximumLocationCount={4}
                          />
                        </TableCell>
                      );

                    case 'assets':
                      return (
                        <TableCell key={column} sx={sx} align={alignment}>
                          <ProjectAssignmentsLabel project={project} />
                        </TableCell>
                      );

                    case 'start-date':
                      return (
                        <TableCell key={column} sx={sx} align={alignment}>
                          <ProjectStartDateLabel
                            variant="body2"
                            project={project}
                            formatOptions={DATE_FORMAT_OPTIONS}
                          />
                        </TableCell>
                      );

                    case 'end-date':
                      return (
                        <TableCell key={column} sx={sx} align={alignment}>
                          <ProjectEndDateLabel
                            variant="body2"
                            project={project}
                            formatOptions={DATE_FORMAT_OPTIONS}
                          />
                        </TableCell>
                      );

                    case 'timeline':
                      return (
                        <TableCell key={column} sx={sx} align={alignment}>
                          <ProjectDateRangeControl
                            project={project}
                            editable={false}
                          />
                        </TableCell>
                      );

                    case 'workflow-stage':
                      return (
                        <TableCell key={column} sx={sx} align={alignment}>
                          <WorkflowStageIndicator project={project} />
                        </TableCell>
                      );

                    case 'billing-summary': {
                      return (
                        <TableCell key={column} sx={sx} align={alignment}>
                          <ProjectBillingChart
                            projects={[project]}
                            variant="mini"
                            showBottomTotal
                            currencyCode={
                              deriveProjectCurrency(
                                project.items.flatMap((lineItem) =>
                                  lineItem.charges.map(
                                    (charge) => charge.totalPrice
                                  )
                                )
                              ) ?? undefined
                            }
                          />
                        </TableCell>
                      );
                    }

                    default:
                      return column satisfies never;
                  }
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {props.projects.length === 0 && (
          <Typography color="text.secondary" padding={2} textAlign="center">
            {emptyState}
          </Typography>
        )}
      </TableContainer>
    </Suspense>
  );
}
